import React from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import theme from '../../styles/theme';

const PaginacaoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  margin-top: ${theme.spacing[6]};
`;

const Info = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PageButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  background: ${props => props.$active ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$active ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$active ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$active ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const NavigationButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  padding: 0 ${theme.spacing[2]};
`;

const ItemsPerPageSelect = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: ${theme.spacing[4]};

  &:hover {
    border-color: ${theme.colors.primary[500]};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${theme.colors.primary[500]}20;
  }
`;

const ItemsPerPageLabel = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  margin-right: ${theme.spacing[2]};
`;

const ParametrosWhatsappPaginacao = ({ pagination, onPageChange, onItemsPerPageChange, loading }) => {
  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPrevPage
  } = pagination;

  if (totalPages <= 1) {
    return null;
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !loading) {
      onPageChange(page);
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    if (onItemsPerPageChange && newItemsPerPage !== itemsPerPage) {
      onItemsPerPageChange(newItemsPerPage);
    }
  };

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <PaginacaoContainer>
      <Info>
        <ItemsPerPageLabel>Registros por página:</ItemsPerPageLabel>
        <ItemsPerPageSelect
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          disabled={loading}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </ItemsPerPageSelect>
        Mostrando {startItem} a {endItem} de {totalItems} parâmetros
      </Info>

      <Controls>
        {/* Primeira página */}
        <NavigationButton
          onClick={() => handlePageChange(1)}
          disabled={!hasPrevPage || loading}
          title="Primeira página"
        >
          <FiChevronsLeft />
        </NavigationButton>

        {/* Página anterior */}
        <NavigationButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPrevPage || loading}
          title="Página anterior"
        >
          <FiChevronLeft />
        </NavigationButton>

        {/* Páginas numeradas */}
        {getVisiblePages().map((page, index) => {
          if (page === '...') {
            return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
          }

          return (
            <PageButton
              key={page}
              $active={page === currentPage}
              onClick={() => handlePageChange(page)}
              disabled={loading}
            >
              {page}
            </PageButton>
          );
        })}

        {/* Próxima página */}
        <NavigationButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNextPage || loading}
          title="Próxima página"
        >
          <FiChevronRight />
        </NavigationButton>

        {/* Última página */}
        <NavigationButton
          onClick={() => handlePageChange(totalPages)}
          disabled={!hasNextPage || loading}
          title="Última página"
        >
          <FiChevronsRight />
        </NavigationButton>
      </Controls>
    </PaginacaoContainer>
  );
};

export default ParametrosWhatsappPaginacao;
